import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import axiosInstance from "../axiosInstance";
import useError from "../hooks/useError";
import { isJobActive } from "../components/Backup/jobUtils";
import { createChunkWithWorker, processExcelWithWorker } from "../components/VehicleSpecs/excelWorker";

/**
 * Lives in MainLayout, so it never unmounts while the user moves between pages.
 * - Polls the server for the running backup/restore job (state is server-side, so it also
 *   survives refreshes and is shared across tabs and users).
 * - Runs the Vehicle Specs chunk upload (refused while a backup/restore is active).
 */

const BackgroundJobContext = createContext(null);
export const useBackgroundJobs = () => useContext(BackgroundJobContext);

const CHUNK_SIZE = 1000;
const IMPORT_IDLE = { phase: "idle" };
const IMPORT_BUSY_PHASES = ["reading", "uploading"];
const POLL_ACTIVE_MS = 10000;
const POLL_IDLE_MS = 60000;
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const apiError = (e) => e?.response?.data?.error || e?.message || "Something went wrong";

export const BackgroundJobProvider = ({ children }) => {
  const handleError = useError();
  const [server, setServer] = useState({ job: null, last_finished: null, worker_alive: true, import_active: false });
  const [finished, setFinished] = useState(null);
  const [importState, setImportState] = useState(IMPORT_IDLE);
  const importRef = useRef(IMPORT_IDLE);
  const trackedRef = useRef(null);
  const ownJobsRef = useRef(new Set());
  const timerRef = useRef(null);
  const tickRef = useRef(null);
  const serverRef = useRef(null);

  const updateImport = useCallback((patch) => {
    importRef.current = typeof patch === "function" ? patch(importRef.current) : { ...importRef.current, ...patch };
    setImportState(importRef.current);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get("Backup/active");
      const state = data.data;
      const tracked = trackedRef.current;
      if (tracked && state.job?.id !== tracked) {
        // The job we were watching has finished: fetch and keep its final state.
        try {
          const res = await axiosInstance.get(`Backup/status/${tracked}`);
          setFinished(res.data.data);
        } catch (e) {
          // ignore, the list page still shows it
        }
      }
      trackedRef.current = state.job?.id || null;
      serverRef.current = state;
      setServer(state);
      return state;
    } catch (e) {
      if (e?.response?.status === 401) handleError(e);
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll every 10s while a backup/restore is active, every minute otherwise. kick() polls now.
  useEffect(() => {
    let stopped = false;
    const tick = async () => {
      clearTimeout(timerRef.current);
      const state = await refresh();
      if (stopped) return;
      timerRef.current = setTimeout(tick, isJobActive(state?.job) ? POLL_ACTIVE_MS : POLL_IDLE_MS);
    };
    tickRef.current = tick;
    tick();
    return () => {
      stopped = true;
      clearTimeout(timerRef.current);
    };
  }, [refresh]);

  const kick = useCallback(() => tickRef.current && tickRef.current(), []);

  const importBusy = IMPORT_BUSY_PHASES.includes(importState.phase);
  const serverBusy = isJobActive(server.job);

  // Warn before leaving the app while this tab is importing or waiting on its own job.
  useEffect(() => {
    const ownJobActive = serverBusy && ownJobsRef.current.has(server.job.id);
    if (!importBusy && !ownJobActive) return;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "A database job is running. Leave anyway?";
      return e.returnValue;
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [importBusy, serverBusy, server.job]);

  const queueBackup = useCallback(async (reason, sourceFileName = null) => {
    const { data } = await axiosInstance.post("Backup/create", { reason, source_file_name: sourceFileName });
    ownJobsRef.current.add(data.data.id);
    trackedRef.current = data.data.id;
    setFinished(null);
    kick();
    return data.data;
  }, [kick]);

  const queueRestore = useCallback(async (backupId) => {
    const { data } = await axiosInstance.post(`Backup/restore/${backupId}`);
    ownJobsRef.current.add(data.data.id);
    trackedRef.current = data.data.id;
    setFinished(null);
    kick();
    return data.data;
  }, [kick]);

  const cancelJob = useCallback(async (id) => {
    const { data } = await axiosInstance.post(`Backup/cancel/${id}`);
    kick();
    return data.data;
  }, [kick]);

  const dismissFinished = useCallback(() => setFinished(null), []);

  const resetImport = useCallback(() => {
    if (!IMPORT_BUSY_PHASES.includes(importRef.current.phase)) updateImport(() => IMPORT_IDLE);
  }, [updateImport]);

  /** Starts the Vehicle Specs import. Errors are shown here; the promise always resolves. */
  const startImport = useCallback(async ({ file, operation }) => {
    if (IMPORT_BUSY_PHASES.includes(importRef.current.phase)) {
      toast.error("An import is already running.");
      return;
    }
    // Re-check the server right before starting; it also refuses with 409 if a job starts meanwhile.
    const latest = (await refresh()) || serverRef.current;
    if (isJobActive(latest?.job)) {
      toast.error(`A database ${latest.job.type} is in progress. Import is available once it finishes.`);
      return;
    }
    const startedAt = Date.now();
    updateImport(() => ({
      phase: "reading",
      step: "Reading Excel file...",
      progress: 10,
      operation,
      fileInfo: { name: file.name, size: file.size, type: file.type, lastModified: file.lastModified },
      originalRowCount: 0,
      cleanedRowCount: 0,
      totalChunks: 0,
      currentChunk: 0,
      chunkProgress: 0,
      chunkResults: [],
      totalStats: { received: 0, inserted: 0, updated: 0 },
      error: null,
      startedAt,
    }));
    kick();

    let progressInterval;
    try {
      let buffer;
      try {
        buffer = await file.arrayBuffer();
      } catch (e) {
        // Chrome invalidates a picked file if it changes on disk (saved again in Excel, moved, still downloading).
        throw new Error("The selected file can no longer be read: it was changed, moved or is still open in Excel after you picked it. Close it in Excel, select the file again and click Upload. Nothing was imported.");
      }
      updateImport({ step: "Processing Excel data...", progress: 20 });
      progressInterval = setInterval(() => updateImport((s) => ({ ...s, progress: Math.min((s.progress || 0) + 2, 45) })), 300);
      const { data: rows, originalRowCount, cleanedRowCount } = await processExcelWithWorker(buffer);
      clearInterval(progressInterval);

      if (originalRowCount > cleanedRowCount) {
        const removed = originalRowCount - cleanedRowCount;
        toast.success(
          `Data cleaned! Removed ${removed.toLocaleString()} empty rows. Reduced from ${originalRowCount.toLocaleString()} to ${cleanedRowCount.toLocaleString()} rows (${((removed / originalRowCount) * 100).toFixed(1)}% reduction)`,
          { duration: 6000 },
        );
      } else {
        toast.success(`Excel file processed successfully! ${cleanedRowCount.toLocaleString()} rows ready for upload.`, { duration: 4000 });
      }

      const header = rows[0] || [];
      const dataRows = rows.slice(1);
      if (dataRows.length === 0) throw new Error("The file has no data rows. Nothing was imported.");
      const totalChunks = Math.ceil(dataRows.length / CHUNK_SIZE);
      updateImport({ originalRowCount, cleanedRowCount, totalChunks, progress: 50 });

      updateImport({ phase: "uploading", step: `Uploading ${totalChunks} chunks...`, progress: 0 });
      const results = [];
      const totals = { received: 0, inserted: 0, updated: 0 };
      for (let i = 0; i < totalChunks; i++) {
        const chunkRows = dataRows.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
        updateImport({ currentChunk: i + 1, chunkProgress: 0 });
        const chunkInterval = setInterval(() => updateImport((s) => ({ ...s, chunkProgress: Math.min((s.chunkProgress || 0) + 15, 80) })), 200);
        let stop = false;
        try {
          const base64Chunk = await createChunkWithWorker(header, chunkRows);
          updateImport({ chunkProgress: 85 });
          const response = await axiosInstance.post("VehicleSpecs/uploadData", {
            operation,
            base64_file: base64Chunk,
            is_first_chunk: i === 0,
          });
          const d = response.data?.data || {};
          const received = d.received || chunkRows.length;
          totals.received += received;
          totals.inserted += d.inserted || 0;
          totals.updated += d.updated || 0;
          results.push({ chunk: i + 1, status: "success", received, inserted: d.inserted || 0, updated: d.updated || 0, message: "Success", uploadTime: Date.now() });
        } catch (e) {
          if (e?.response?.status === 401) throw e;
          results.push({ chunk: i + 1, status: "error", message: apiError(e), received: chunkRows.length, inserted: 0, updated: 0, uploadTime: Date.now() });
          // 409 = blocked by a backup/restore; a failed first Overwrite chunk would keep loading into a truncated table.
          stop = e?.response?.status === 409 || (i === 0 && operation === "overwrite");
        } finally {
          clearInterval(chunkInterval);
        }
        updateImport({
          chunkProgress: 100,
          progress: Math.round(((i + 1) / totalChunks) * 100),
          chunkResults: [...results],
          totalStats: { ...totals },
        });
        if (stop) {
          throw new Error(`Import stopped at chunk ${i + 1} of ${totalChunks}: ${results[results.length - 1].message}`);
        }
        await delay(50);
      }

      const failed = results.filter((r) => r.status === "error").length;
      updateImport({ phase: "done", progress: 100, currentChunk: totalChunks, chunkProgress: 0, finishedAt: Date.now(), failedChunks: failed });
      if (failed) toast.error(`Import finished with ${failed} failed chunk(s). Review the details.`, { duration: 6000 });
      else toast.success("File Uploaded Successfully!", { duration: 5000 });
    } catch (e) {
      clearInterval(progressInterval);
      updateImport({ phase: "failed", error: apiError(e), finishedAt: Date.now(), chunkProgress: 0 });
      if (e?.response?.status === 401) handleError(e);
      else toast.error(apiError(e), { duration: 8000 });
    } finally {
      kick();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kick, refresh, updateImport]);

  const value = useMemo(
    () => ({
      server,
      finished,
      importState,
      importBusy,
      serverBusy,
      busy: importBusy || serverBusy,
      refresh: kick,
      queueBackup,
      queueRestore,
      cancelJob,
      dismissFinished,
      startImport,
      resetImport,
    }),
    [server, finished, importState, importBusy, serverBusy, kick, queueBackup, queueRestore, cancelJob, dismissFinished, startImport, resetImport],
  );

  return <BackgroundJobContext.Provider value={value}>{children}</BackgroundJobContext.Provider>;
};
